import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { isEmpty } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import NavButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/navButton/NavButton'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import Label from '@lyrahealth-inc/ui-core/lib/atoms/label/Label'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'

import styles from './activitiesDashlet.module.scss'
import { getAiUnreadClientActivities } from './activitiesDashletUtils'
import { CLIENT_ACTIVITIES, MESSENGER } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { useClientsData } from '../../clients/data/hooks/useClientsData'
import { useGetClientsCountsQuery } from '../../clients/data/ltClientsApi'
import { useConversationsData } from '../../messages/data/hooks/useConversationsData'
import { clearSelectedConversation, setPendingInitialThreadId } from '../../messages/data/messagesSlice'
import { getAiThreadId } from '../../messages/data/threadUtils'

enum ActivitiesDashletTab {
  ACTIVITIES = 'Activities',
  LYRA_AI = 'Lyra AI',
}

const ActivitiesDashlet: React.FC<ActivitiesDashletProps> = ({ providerId }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState(ActivitiesDashletTab.ACTIVITIES)
  const countsEndPointExcludedContentGroups = ['lesson', 'infosheet']
  const {
    data: clientsCounts,
    refetch: refetchClientsCounts,
    isSuccess: hasFetchedCounts,
  } = useGetClientsCountsQuery({
    providerId,
    excludeContentGroups: countsEndPointExcludedContentGroups,
  })

  const { data: activeClients, isSuccess: activeClientsFetched, refetch: refetchActiveClients } = useClientsData()
  const [isRefreshingClients, setIsRefreshingClients] = useState(false)
  const { isPreferredNameEnabled } = useFlags()

  const {
    data: aiCoachConversations,
    isLoading: isLoadingAiCoach,
    refetch: refetchAiCoachConversations,
  } = useConversationsData({ types: CONVERSATION_TYPE.AI_COACH })

  const activitiesClientsWithUnread = useMemo(() => {
    if (!Array.isArray(activeClients) || isEmpty(clientsCounts)) return []
    return activeClients.filter(
      (client) => clientsCounts && client.id in clientsCounts && clientsCounts[client.id][0].unread_responses > 0,
    )
  }, [activeClients, clientsCounts])

  const aiUnreadClientActivities = useMemo(
    () => getAiUnreadClientActivities(activeClients ?? [], aiCoachConversations ?? []),
    [activeClients, aiCoachConversations],
  )

  const activitiesClientCount = activitiesClientsWithUnread.length
  const aiCoachClientCount = aiUnreadClientActivities.length

  const additiveClientCount = activitiesClientCount + aiCoachClientCount

  const renderClientItem = (
    client: ClientListClientObject,
    unreadCount: number,
    testIdPrefix: string,
    onClick: () => void,
  ) => {
    return (
      <div
        className={styles['dashlet-item-container']}
        data-test-id={`ActivitiesDashlet-${testIdPrefix}-item-${client.first_name}`}
        key={client.id}
      >
        <button type='button' className={styles['dashlet-item-content']} onClick={onClick}>
          <div>
            <div className={styles.clientName}>{getClientFullName(client, isPreferredNameEnabled)}</div>
          </div>
          <div className={styles.right}>
            <Label
              textColor={styles.x_white}
              backgroundColor={styles.x_alert}
              borderRadius='6px'
              padding='0 7px'
              text={unreadCount}
            />
          </div>
        </button>
      </div>
    )
  }

  const isLoading = !activeClientsFetched || !hasFetchedCounts || isLoadingAiCoach
  const hasCounts = activeClientsFetched && hasFetchedCounts && !isLoadingAiCoach
  const showTabs = aiCoachClientCount > 0 && !isLoading
  const visibleTab = showTabs ? activeTab : ActivitiesDashletTab.ACTIVITIES

  const renderActivitiesContent = () => {
    if (activitiesClientCount === 0) {
      return <div className={styles['empty-dashlet']}>No unreviewed activities</div>
    }
    // PROVIDER-2435: we are temporarily disabling the overdue flag on this dashlet
    return activitiesClientsWithUnread.map((client) =>
      renderClientItem(client, clientsCounts?.[client.id][0].unread_responses ?? 0, 'activities', () => {
        dispatch(setSelectedClientId(client.id))
        navigate(CLIENT_ACTIVITIES.route)
      }),
    )
  }

  const renderAiCoachContent = () => {
    if (aiCoachClientCount === 0) {
      return <div className={styles['empty-dashlet']}>No unread Lyra AI messages</div>
    }
    return aiUnreadClientActivities.map(({ client, unreadCount, conversationId }) => {
      return renderClientItem(client, unreadCount, 'lyra-ai', () => {
        dispatch(setSelectedClientId(client.id))
        dispatch(clearSelectedConversation())
        dispatch(setPendingInitialThreadId(getAiThreadId(conversationId)))
        navigate(MESSENGER.route)
      })
    })
  }

  return (
    <div className={styles['dashlet-container']} data-test-id='ActivitiesDashlet-container'>
      <Dashlet
        title={`Unreviewed activities${!hasCounts ? '' : ` (${additiveClientCount})`}`}
        buttons={
          <RefreshButton
            size={'20'}
            isLoading={isRefreshingClients}
            data-test-id='ActivitiesDashlet-refreshButton'
            onClick={() => {
              setIsRefreshingClients(true)
              Promise.all([refetchClientsCounts(), refetchActiveClients(), refetchAiCoachConversations()]).then(() => {
                setIsRefreshingClients(false)
              })
            }}
          />
        }
        isLoading={isLoading}
        tabs={
          showTabs && (
            <>
              <NavButton
                text={`${ActivitiesDashletTab.ACTIVITIES} ${activitiesClientCount}`}
                isSelected={visibleTab === ActivitiesDashletTab.ACTIVITIES}
                onClick={() => setActiveTab(ActivitiesDashletTab.ACTIVITIES)}
                styleType='dashlet'
                data-test-id='ActivitiesDashlet-Tab-activities'
              />
              <NavButton
                text={`${ActivitiesDashletTab.LYRA_AI} ${aiCoachClientCount}`}
                isSelected={visibleTab === ActivitiesDashletTab.LYRA_AI}
                onClick={() => setActiveTab(ActivitiesDashletTab.LYRA_AI)}
                styleType='dashlet'
                data-test-id='ActivitiesDashlet-Tab-lyraAi'
              />
            </>
          )
        }
      >
        {visibleTab === ActivitiesDashletTab.ACTIVITIES ? renderActivitiesContent() : renderAiCoachContent()}
      </Dashlet>
    </div>
  )
}

type ActivitiesDashletProps = {
  providerId: string
}
export default ActivitiesDashlet
